import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';

import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';
import type { MetricFormat } from '../../../../config/metrics';
import { DEFAULT_METRIC_FORMAT } from '../../../../config/metrics';
import type { ConstraintInputType } from '../../../../config/optimizePlanConfig';
import type { ConstraintCatalogEntry, Level } from '../../components/OptimizePlan/types';

/** One attribute's baselines at one level. */
export interface ConstraintAttributeValues {
    attribute_name: string;
    /** The owning attribute at the level above; `null` at the topmost level. Parsed, not yet used. */
    parent_attribute_name: string | null;
    original_value: number | null;
    full_year_original_value: number | null;
}

/** Every constraint's rows at one level, keyed by constraint name. */
export type LevelValues = Map<string, ConstraintAttributeValues[]>;

/** Everything the one request needs. `level` is the coarsest level the scope allows. */
export interface ConstraintValuesContext {
    planId: string;
    objective: string;
    startDate: number;
    level: Level;
    attributeList: string[];
}

export type ConstraintValuesStatus = 'loading' | 'error' | 'ready';

export interface ConstraintValuesResult {
    /**
     * The KPI catalog for the Select Constraints popover, in response order.
     * Empty until the fetch resolves, which is what keeps the selector disabled.
     */
    catalog: ConstraintCatalogEntry[];
    /** Baselines for the requested level and every level below it. */
    byLevel: Map<Level, LevelValues>;
    status: ConstraintValuesStatus;
}

const EMPTY_RESULT: ConstraintValuesResult = {
    catalog: [],
    byLevel: new Map(),
    status: 'ready',
};

// --- Response parsing --------------------------------------------------------

interface RawLevelDetail {
    attribute_name?: unknown;
    parent_attribute_name?: unknown;
    details?: Record<string, unknown>;
}

interface RawConstraintDetail {
    name?: unknown;
    label?: unknown;
    constraint_type?: unknown;
    level_details?: RawLevelDetail[];
}

interface RawLevel {
    level?: unknown;
    constraint_details?: RawConstraintDetail[];
}

/** What the query caches: the parsed tree plus the catalog derived from it. */
export interface ParsedConstraintValues {
    catalog: ConstraintCatalogEntry[];
    byLevel: Map<Level, LevelValues>;
}

const readNumber = (value: unknown): number | null => (typeof value === 'number' ? value : null);

const readString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

function normalizeAttribute(raw: RawLevelDetail): ConstraintAttributeValues | null {
    const attributeName = readString(raw.attribute_name);
    if (attributeName == null) return null;

    // The numbers live under `details`; tolerate a flat shape too.
    const source = (raw.details && typeof raw.details === 'object' ? raw.details : raw) as Record<string, unknown>;
    return {
        attribute_name: attributeName,
        parent_attribute_name: readString(raw.parent_attribute_name),
        original_value: readNumber(source.original_value),
        full_year_original_value: readNumber(source.full_year_original_value),
    };
}

/**
 * Flattens `levels[] → constraint_details[] → level_details[]` into a
 * level → constraint → attributes tree, collecting the catalog on the way.
 *
 * The catalog is a union across levels rather than a read of the first level, so
 * a KPI that only exists further down the hierarchy is still selectable. First
 * occurrence wins for label and input type; in practice every level repeats the
 * same list.
 */
export function parseConstraintValuesResponse(
    raw: unknown,
    metricFormatByKey: Map<string, MetricFormat>,
): ParsedConstraintValues {
    const levels = ((raw as { levels?: RawLevel[] } | null)?.levels ?? []) as RawLevel[];

    const catalog: ConstraintCatalogEntry[] = [];
    const seen = new Set<string>();
    const byLevel = new Map<Level, LevelValues>();

    for (const rawLevel of levels) {
        const level = readString(rawLevel.level);
        if (level == null) continue;

        const levelValues: LevelValues = new Map();

        for (const detail of rawLevel.constraint_details ?? []) {
            const name = readString(detail.name);
            if (name == null) continue;

            if (!seen.has(name)) {
                seen.add(name);
                catalog.push({
                    key: name,
                    label: readString(detail.label) ?? name,
                    // `range` until the backend starts sending `constraint_type`; every
                    // constraint shipped so far is a min/max range.
                    inputType: (detail.constraint_type === 'number' ? 'number' : 'range') as ConstraintInputType,
                    format: metricFormatByKey.get(name) ?? DEFAULT_METRIC_FORMAT,
                });
            }

            const attributes: ConstraintAttributeValues[] = [];
            for (const rawAttribute of detail.level_details ?? []) {
                const attribute = normalizeAttribute(rawAttribute);
                if (attribute) attributes.push(attribute);
            }
            levelValues.set(name, attributes);
        }

        byLevel.set(level, levelValues);
    }

    return { catalog, byLevel };
}

function buildRequest(context: ConstraintValuesContext) {
    return {
        plan_id: context.planId,
        objective: context.objective,
        start_date: context.startDate,
        is_tpm_plan: false,
        level: context.level,
        attribute_list: context.attributeList,
    };
}

async function fetchConstraintValues(
    request: ReturnType<typeof buildRequest>,
    metricFormatByKey: Map<string, MetricFormat>,
    signal?: AbortSignal,
): Promise<ParsedConstraintValues> {
    const response = await axios(tpoGenUrls.getConstraintsListOriginalValues, {
        method: 'POST',
        data: request,
        signal,
    });
    return parseConstraintValuesResponse(response.data?.data, metricFormatByKey);
}

/**
 * Fetches every constraint's baselines for the scoped level and all levels below
 * it, in one request.
 *
 * Deliberately independent of what the user has selected: the response is the
 * catalog as well as the values, so it must resolve before the Select Constraints
 * popover has anything to offer. Adding or removing a level after that is pure
 * UI — the numbers are already here.
 */
export function useConstraintOriginalValues({
    context,
    metricFormatByKey,
    enabled,
}: {
    context: ConstraintValuesContext | null;
    metricFormatByKey: Map<string, MetricFormat>;
    enabled: boolean;
}): ConstraintValuesResult {
    const request = context != null ? buildRequest(context) : null;
    const active = enabled && request != null;

    const { data, isPending, isError } = useQuery({
        queryKey: tpoGenKeys.constraintOriginalValues(request ?? {}),
        queryFn: ({ signal }) => fetchConstraintValues(request!, metricFormatByKey, signal),
        enabled: active,
        refetchOnWindowFocus: false,
    });

    return useMemo(() => {
        // A disabled query reports `isPending`, so gate on `active` first: with no
        // objective yet there is nothing in flight and nothing to show.
        if (!active) return EMPTY_RESULT;
        if (isPending) return { ...EMPTY_RESULT, status: 'loading' };
        if (isError || !data) return { ...EMPTY_RESULT, status: 'error' };
        return { catalog: data.catalog, byLevel: data.byLevel, status: 'ready' };
    }, [data, isPending, isError, active]);
}
